import {
  Injectable,
  ApplicationRef,
  ComponentFactoryResolver,
  ComponentRef,
  EmbeddedViewRef,
  Injector,
  InjectionToken,
} from "@angular/core";
import { Subject, Observable } from "rxjs";
import { DialogRef } from "./dialog-ref";

export const DIALOG_DATA = new InjectionToken("DIALOG_DATA");

export interface DialogConfig {
  data?: any;
  width?: string;
  panelClass?: string;
  autoFocus?: boolean;
}

@Injectable({ providedIn: "root" })
export class DialogService {
  private activeDialogs: ComponentRef<any>[] = [];
  private readonly afterAllClosedSubject$ = new Subject<void>();

  constructor(
    private readonly resolver: ComponentFactoryResolver,
    private readonly injector: Injector,
    private readonly appRef: ApplicationRef,
  ) {}

  open<T>(component: any, config: DialogConfig = {}): DialogRef<T> {
    const dialogRef = new DialogRef<T>(this);

    // Create Injector with DialogRef and DIALOG_DATA
    const dialogInjector = this.createInjector(config.data, dialogRef);

    // Dynamically create the component
    const factory = this.resolver.resolveComponentFactory(component);
    const dialogComponentRef = factory.create(dialogInjector);

    // Configure modal styles
    const dialogElement = (dialogComponentRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;

    if (config.panelClass) {
      dialogElement.classList.add(config.panelClass);
    }

    if (config.width) {
      // eslint-disable-next-line fp/no-mutation
      dialogElement.style.width = config.width;
    }

    // Attach to the DOM
    this.appRef.attachView(dialogComponentRef.hostView);
    document.body.appendChild(dialogElement);

    // Track active dialogs
    this.activeDialogs.push(dialogComponentRef);

    // eslint-disable-next-line fp/no-mutation
    dialogRef.componentRef = dialogComponentRef;

    return dialogRef;
  }

  close<T>(dialogRef: DialogRef<T>, result?: T): void {
    const index = this.activeDialogs.findIndex((ref) => ref.instance === dialogRef.componentRef?.instance);

    if (index >= 0) {
      const dialogComponentRef = this.activeDialogs[index];
      this.appRef.detachView(dialogComponentRef.hostView);
      dialogComponentRef.destroy();
      this.activeDialogs.splice(index, 1);
    }

    dialogRef.close(result);

    // If no dialogs are left, notify observers
    if (this.activeDialogs.length === 0) {
      this.afterAllClosedSubject$.next();
    }
  }

  closeAll(): void {
    this.activeDialogs.forEach((dialogRef) => {
      this.appRef.detachView(dialogRef.hostView);
      dialogRef.destroy();
    });
    this.activeDialogs = [];

    this.afterAllClosedSubject$.next();
  }

  get afterAllClosed$(): Observable<void> {
    return this.afterAllClosedSubject$.asObservable();
  }

  private createInjector(data: any, dialogRef: DialogRef): Injector {
    return Injector.create({
      providers: [
        { provide: DialogRef, useValue: dialogRef },
        { provide: DIALOG_DATA, useValue: data },
      ],
      parent: this.injector,
    });
  }
}
